// Home: child tabs, schedule widget, story list, referral modal.
import { useState, useEffect } from "react";
import { Plus, Star, Sparkles, Play, Music, Check, TrendingUp, ClipboardList, Gift, Copy, Send, Users, X, Pencil, Target, Smartphone, Lock, UserPlus } from "lucide-react";
import { MagicSpinner } from "../../Onboarding.jsx";
import { LangToggle, useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { BACK_PRIORITY, useHostBackButton } from "../backbutton.js";
import { addToHomeScreen, hasSettingsButton, haptic, useHomeScreenOffer } from "../host.js";
import { messengerName } from "../platform.js";
import { childAgeLabel, dayName, fmtDate, planLabel } from "../constants.js";
import { EmojiAvatar, FluentEmoji, Stars } from "../emoji.jsx";
import { fmt, fmtTime, formatCountdown, toFa, useCountdown } from "../format.js";
import { C, FONT, BLACK, alpha } from "../../design-system.js";
import { SquircleBg, InfoBanner, AdSlot } from "../ui.jsx";
import { Btn } from "../../admin/ui.jsx";
import { TaleShelf } from "../tales/TaleCard.jsx";

// ── Schedule Widget ───────────────────────────────────────────────────────────
export function ScheduleWidget({ schedule, childName }) {
  const { t } = useLang();
  const { msLeft, nextSlot } = useCountdown(schedule);
  const cd = msLeft !== null ? formatCountdown(msLeft) : null;

  return (
    <div style={{ background: `${C.gold}0A`, border: `1px solid ${C.borderGold}`, borderRadius: 16, padding: "14px 16px", marginBottom: 12 }}>
      {/* Fixed weekly days */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ color: C.gold, fontSize: 15 }}>📅</span>
        <span style={{ color: C.cream, fontSize: 14, fontWeight: 600 }}>{t("زمان‌بندی قصه‌های {name}", { name: childName })}</span>
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        {[schedule.slot1, schedule.slot2].map((s, i) => (
          <div key={i} style={{ flex: 1, background: C.bgDeep, borderRadius: 12, padding: "9px 12px", textAlign: "center" }}>
            <div style={{ color: C.gold, fontSize: 14, fontWeight: 700 }}>{dayName(s.day)}</div>
            <div style={{ color: C.cream, fontSize: 16, fontWeight: 800, marginTop: 3 }}>{fmtTime(s.hour, s.minute)}</div>
          </div>
        ))}
      </div>

      {/* Countdown */}
      {cd && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: cd.pulse ? `${C.teal}15` : C.bgDeep, borderRadius: 11, border: `1px solid ${cd.pulse ? C.teal + "44" : C.border}` }}>
          <span style={{ fontSize: 16, animation: cd.pulse ? "pulseDot 1.2s ease-in-out infinite" : "none" }}>
            {cd.pulse ? "🎉" : "⏱"}
          </span>
          <div>
            <div style={{ color: cd.pulse ? C.teal : C.textMid, fontSize: 13 }}>
              {cd.pulse ? t("قصه جدید") : t("قصه بعدی")}
              {nextSlot && !cd.pulse && <span style={{ color: C.textDim }}> — {dayName(nextSlot.day)} {fmtTime(nextSlot.hour, nextSlot.minute)}</span>}
            </div>
            <div style={{ color: cd.pulse ? C.teal : C.cream, fontSize: 16, fontWeight: 700, marginTop: 2 }}>{cd.text}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Referral Modal ────────────────────────────────────────────────────────────
// Invite counts that earn a free plan. The SERVER owns them (referral.py
// FREE_PLAN_THRESHOLDS, shipped as `thresholds` on /referral/my-info); this is
// only the fallback for a response that predates the field.
const FREE_PLAN_THRESHOLDS = { 1: 5, 2: 9, 3: 13 };
const PLAN_LABELS = { 1: "۱ ماه", 2: "۲ ماه", 3: "۳ ماه" };

export function ReferralModal({ onClose }) {
  const { t } = useLang();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  // "" | "sending" | "sent" | "error" — the DM-me-my-invite button's own state.
  const [inviteState, setInviteState] = useState("");

  // An overlay owns the host back arrow while it's up: back closes the modal,
  // not the screen underneath it.
  useHostBackButton(onClose, { priority: BACK_PRIORITY.modal });

  useEffect(() => {
    api("/referral/my-info")
      .then(data => setR(data))
      .catch(() => setR(null))
      .finally(() => setLoading(false));
  }, []);

  function copy(text) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Ask the bot to DM this parent their own invite, so they can FORWARD it.
  //
  // ⚠️ There is no in-app share sheet to use instead. Bale's SDK has no share or
  // forward method at all; Telegram's and Soroush's switchInlineQuery/shareMessage
  // need inline mode and savePreparedInlineMessage (Soroush 404s on the underlying
  // answerWebAppQuery); Eitaa has neither. Forwarding a bot message is the one
  // share path that exists on all four — see POST /referral/send-invite.
  function sendInvite() {
    if (inviteState === "sending") return;
    haptic("light");
    setInviteState("sending");
    api("/referral/send-invite", { method: "POST" })
      .then(() => { setInviteState("sent"); haptic("success"); })
      .catch(() => setInviteState("error"));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: alpha(BLACK, 0.7), zIndex: 60, display: "flex", alignItems: "flex-end", justifyContent: "center", animation: "fadeIn 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.bgCard, borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "24px 20px 40px", animation: "slideUp 0.3s ease", border: `1px solid ${C.borderGold}`, borderBottom: "none" }}>
        {/* Handle */}
        <div style={{ width: 40, height: 4, background: C.border, borderRadius: 4, margin: "0 auto 20px" }} />

        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <MagicSpinner size={44} />
            <div style={{ color: C.textMid, fontSize: 15, marginTop: 16 }}>{t("در حال بارگذاری...")}</div>
          </div>
        ) : !r ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div style={{ color: C.red, fontSize: 15 }}>{t("خطا در بارگذاری اطلاعات")}</div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ color: C.cream, fontSize: 19, fontWeight: 800 }}>{t("دعوت دوستان")}</div>
                <div style={{ color: C.textMid, fontSize: 13, marginTop: 3 }}>{t("تخفیف برای شما و دوستانتان")}</div>
              </div>
              <button className="press" onClick={onClose} style={{ background: C.bgDeep, border: `1px solid ${C.border}`, color: C.textMid, borderRadius: 10, width: 36, height: 36, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={17} />
              </button>
            </div>

            {/* Free-plan milestones — invite active subscribers, earn a free plan */}
            <div style={{ background: C.bgDeep, borderRadius: 16, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <span style={{ color: C.cream, fontSize: 15, fontWeight: 700 }}>{t("دعوت‌های موفق شما")}</span>
                <span style={{ color: C.gold, fontSize: 16, fontWeight: 800 }}><Users size={15} style={{ verticalAlign: "-2px" }} /> {t("{n} نفر", { n: toFa(r.active_invites) })}</span>
              </div>
              {[1, 2, 3].map(mo => ({ n: Number(r.thresholds?.[mo]) || FREE_PLAN_THRESHOLDS[mo], m: PLAN_LABELS[mo] })).map(({ n, m }, i) => {
                const done = (r.active_invites || 0) >= n;
                const pct = Math.min(100, Math.round((r.active_invites || 0) / n * 100));
                return (
                  <div key={n} style={{ marginBottom: i < 2 ? 12 : 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                      <span style={{ color: done ? C.teal : C.cream, fontSize: 13.5, fontWeight: 600 }}>{done ? "✓ " : "🎁 "}{t("{m} اشتراک رایگان", { m: t(m) })}</span>
                      <span style={{ color: done ? C.teal : C.textMid, fontSize: 12.5 }}>{t("{x} از {n} دعوت", { x: toFa(Math.min(r.active_invites || 0, n)), n: toFa(n) })}</span>
                    </div>
                    <div style={{ height: 7, background: C.border, borderRadius: 7, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: done ? C.teal : `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`, borderRadius: 7, transition: "width 0.6s ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* How it works */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {[["🎁 دوستتان", "۲۰٪ تخفیف روی اولین اشتراک", C.teal, null], ["✨ شما", "با {n} دعوت، یک ماه اشتراک رایگان", C.gold, { n: toFa(Number(r.thresholds?.[1]) || FREE_PLAN_THRESHOLDS[1]) }]].map(([who, what, color, vars]) => (
                <div key={who} style={{ flex: 1, background: `${color}0D`, border: `1px solid ${color}33`, borderRadius: 14, padding: 12, textAlign: "center" }}>
                  <div style={{ color, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t(who)}</div>
                  <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>{t(what, vars || undefined)}</div>
                </div>
              ))}
            </div>

            {/* Code */}
            <div style={{ background: C.bgDeep, border: `1px solid ${C.border}`, borderRadius: 14, padding: "13px 16px", marginBottom: 12 }}>
              <div style={{ color: C.textMid, fontSize: 13, marginBottom: 6 }}>{t("کد دعوت شما")}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ color: C.cream, fontSize: 22, fontWeight: 800, letterSpacing: 3, fontFamily: "monospace" }}>{r.code}</span>
                <button className="press" onClick={() => copy(r.code)}
                  style={{ background: `${C.gold}18`, border: `1px solid ${C.gold}44`, color: C.gold, borderRadius: 10, padding: "8px 16px", fontFamily: FONT, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
                  <Copy size={14} /> {t("کپی کد")}
                </button>
              </div>
            </div>

            <button className="press celebrate" onClick={() => copy(r.link)}
              style={{ width: "100%", background: copied ? `${C.teal}22` : `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: copied ? `1px solid ${C.teal}` : "none", color: copied ? C.teal : C.bgDeep, borderRadius: 14, padding: "14px 0", fontFamily: FONT, fontSize: 16, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {copied ? <><Check size={17} /> {t("لینک کپی شد!")}</> : <><Gift size={17} /> {t("کپی لینک دعوت")}</>}
            </button>

            {/* Send it to me so I can forward it. The messengers give a Mini App no
                share sheet (Bale has no share method at all), but every one of them
                forwards a bot message natively — so the bot writes the invite and
                the parent forwards it. */}
            <button className="press" onClick={sendInvite} disabled={inviteState === "sending"}
              style={{ width: "100%", marginTop: 10, background: inviteState === "sent" ? `${C.teal}18` : "none", border: `1px solid ${inviteState === "sent" ? C.teal : C.borderGold}`, color: inviteState === "sent" ? C.teal : C.gold, borderRadius: 14, padding: "13px 0", fontFamily: FONT, fontSize: 15, fontWeight: 700, cursor: inviteState === "sending" ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {inviteState === "sent"
                ? <><Check size={16} /> {t("دعوت‌نامه فرستاده شد")}</>
                : <><Send size={16} /> {t("دعوت‌نامه را برایم بفرست")}</>}
            </button>
            <div style={{ color: inviteState === "error" ? C.red : C.textMid, fontSize: 12, lineHeight: 1.8, textAlign: "center", marginTop: 8 }}>
              {inviteState === "error"
                ? t("ارسال ناموفق بود. دوباره تلاش کنید.")
                : inviteState === "sent"
                ? t("در گفتگویتان با ربات است — آن را برای دوستانتان فوروارد کنید.")
                : t("یک پیام آماده در {app} برایتان می‌فرستیم که فقط فورواردش کنید.", { app: messengerName() })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function SupportHint({ onSupport, subscribed }) {
  const { t } = useLang();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, margin: "22px 0 8px" }}>
      <Btn variant="outline" onClick={onSupport} color={subscribed ? C.gold : C.creamDim}>
        {t(subscribed ? "پشتیبانی ویژه" : "پشتیبانی")}
      </Btn>
      <span style={{ color: C.textDim, fontSize: 12.5, lineHeight: 1.8, textAlign: "center" }}>
        {t("سؤالی دارید؟ همین‌جا برای ما پیام بفرستید.")}
      </span>
    </div>
  );
}

// ── The three steps (product owner, 2026-10-27) ───────────────────────────────
// Replaces the first-run «افزودن فرزند» screen AND the old one-big-button funnel.
// 1 → 2 are ordered (the questionnaire is ABOUT a named child); 3 stands alone, so
// a parent can see the plans and buy before telling us anything — the plan then
// waits on a placeholder child and its days start counting at the questionnaire.
// `guest` = the Bazaar app before «ورود با بازار», which step 1 starts with.
export function SetupSteps({ child, onAddChild, onEditChild, onQuestionnaire, onSubscribe, guest = false }) {
  const { t } = useLang();
  const named = !!child.name;
  const qDone = !!child.questionnaire_completed;
  const subbed = !!child.subscribed;
  const steps = [
    { n: 1, done: named, title: t("افزودن فرزند"),
      sub: named ? t("{name} اضافه شد", { name: child.name })
        : guest ? t("ورود با بازار، سپس نام، سن و جنسیت")
        : t("نام، سن و جنسیت — کمتر از یک دقیقه"),
      cta: named ? t("ویرایش") : t("افزودن"), icon: <UserPlus size={15} />,
      go: () => (named ? onEditChild(child) : onAddChild()) },
    { n: 2, done: qDone, locked: !named, title: t("پر کردن پرسشنامه"),
      sub: !named ? t("بعد از قدم ۱") : qDone ? t("تکمیل شد — بازی‌های مخصوص {name} باز شد", { name: child.name }) : t("حدود ۱۰ تا ۱۵ دقیقه، رایگان"),
      cta: qDone ? t("ویرایش") : t("شروع"), icon: <ClipboardList size={15} />,
      go: () => onQuestionnaire(child), hideCta: qDone && subbed },
    { n: 3, done: subbed, title: t("انتخاب طرح"),
      sub: subbed ? (qDone ? t("اشتراک فعال است") : t("اشتراک فعال است — روزهایش از تکمیل پرسشنامه شمرده می‌شود"))
        : t("هر وقت خواستید — حتی پیش از دو قدم دیگر"),
      cta: t("دیدن طرح‌ها"), icon: <Star size={15} />,
      go: () => onSubscribe(child.id ? child : null), hideCta: subbed },
  ];
  // the step to point at: the first open one of 1→2, else the plan
  const next = steps.find(st => !st.done && !st.locked && st.n < 3)?.n || (!subbed ? 3 : null);
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.borderGold}`, borderRadius: 16, padding: "14px 14px 6px" }}>
      <div style={{ color: C.gold, fontSize: 14.5, fontWeight: 800, marginBottom: 10 }}>
        {subbed ? t("دو قدم تا اولین قصه") : t("سه قدم تا اولین قصهٔ شخصی")}
      </div>
      {steps.map((st, i) => {
        const active = st.n === next;
        const col = st.done ? C.teal : active ? C.gold : C.textMid;
        return (
          <div key={st.n} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 0", borderTop: i ? `1px solid ${C.border}` : "none", opacity: st.locked ? 0.55 : 1 }}>
            <span style={{ width: 30, height: 30, borderRadius: 999, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
              background: st.done ? `${C.teal}22` : active ? `${C.gold}22` : C.bgCard, border: `1.5px solid ${col}`, color: col, fontWeight: 800, fontSize: 14 }}>
              {st.done ? <Check size={15} /> : st.locked ? <Lock size={13} /> : toFa(st.n)}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", color: st.done ? C.creamDim : C.cream, fontSize: 15, fontWeight: 700 }}>{st.title}</span>
              <span style={{ display: "block", color: C.textMid, fontSize: 12.5, lineHeight: 1.6, marginTop: 1 }}>{st.sub}</span>
            </span>
            {!st.locked && !st.hideCta && (
              <button onClick={() => { haptic("light"); st.go(); }} className={active ? "press celebrate" : "press"}
                style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT, fontSize: 13.5, fontWeight: 800, cursor: "pointer", borderRadius: 12, padding: "9px 13px",
                  background: active ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : "transparent",
                  color: active ? C.bgDeep : st.done ? C.textMid : C.gold,
                  border: active ? "none" : `1px solid ${st.done ? C.border : C.borderGold}` }}>
                {st.icon} {st.cta}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// The «بازی‌ها» door — a peek at the pets that live in the games, not a bare icon.
const GAME_PEEK = ["pet-panda", "pet-bunny", "pet-chick", "pet-cat", "pet-fox"];

// ── Home Screen ───────────────────────────────────────────────────────────────
export function HomeScreen({ children: childrenProp, refreshChildren, userName, activeChild: activeChildProp, setActiveChild: setActiveChildProp, onAddChild, onSelectStory, onSubscribe, onQuestionnaire, onJourney, onGames, onTale, onEditChild, onReferral, onTerms, onSupport, onUpgrade, onGuarantee, guest = false }) {
  const { t } = useLang();
  // Controlled by the App so the selected tab survives navigation away and back.
  const [activeChildLocal, setActiveChildLocal] = useState(0);
  const activeChild = activeChildProp != null ? activeChildProp : activeChildLocal;
  const setActiveChild = setActiveChildProp || setActiveChildLocal;
  const [schedule, setSchedule] = useState(null);
  const [stories, setStories] = useState([]);
  const [subscription, setSubscription] = useState(null);
  // {plan, options} — the longer plans the running one can be upgraded to.
  const [upgrade, setUpgrade] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // "missed" = this host CAN add a home-screen shortcut and the app is not there yet.
  const homeScreenOffer = useHomeScreenOffer(true);
  const [homeAdded, setHomeAdded] = useState(false);

  // Map backend child to display format
  const children = (childrenProp || []).map(c => ({
    ...c,
    age: childAgeLabel(c),
    subscribed: !!c.active_subscription,
  }));

  // No child yet → one VIRTUAL tab, «نام فرزند», whose content is the three steps.
  // (The first-run sphere screen that used to stand here is switched off — the
  // product owner wants a new parent straight in the app. faces.jsx is kept.)
  const tabs = children.length ? children
    : [{ id: null, name: "", avatar: "🌟", age: "", subscribed: false, questionnaire_completed: false, virtual: true }];
  const child = tabs[activeChild] || tabs[0];
  const named = !!child.name;
  // «فرزند جدید» appears only once a child has actually been added (a blank
  // placeholder left by a plan bought first doesn't count).
  const hasNamedChild = children.some(c => !!c.name);
  const setupDone = named && child.subscribed && child.questionnaire_completed;
  // A parent who hasn't taken any step sees only the steps: the «نام فرزند» row
  // with its edit pencil just repeats step 1 and distracts from it.
  const noStepDone = !named && !child.subscribed && !child.questionnaire_completed;

  // Fetch schedule, stories, and subscription when active child changes
  useEffect(() => {
    if (!child.id) { setStories([]); setSchedule(null); setSubscription(null); return; }
    setLoadingDetails(true);
    setSchedule(null);
    setStories([]);
    setSubscription(null);
    setUpgrade(null);

    const fetches = [
      api(`/stories/child/${child.id}`).then(setStories).catch(() => setStories([])),
    ];

    if (child.subscribed) {
      fetches.push(
        api(`/schedule/${child.id}`)
          .then(data => {
            if (data && data.slots && data.slots.length >= 2) {
              setSchedule({ slot1: data.slots[0], slot2: data.slots[1] });
            } else {
              setSchedule(null);
            }
            if (data && data.subscription_expires_at) {
              setSubscription(prev => ({ ...prev, expires_at: data.subscription_expires_at }));
            }
          })
          .catch(() => setSchedule(null))
      );
      fetches.push(
        api(`/subscriptions/child/${child.id}`)
          .then(sub => setSubscription(sub))
          .catch(() => setSubscription(null))
      );
      if (onUpgrade) fetches.push(
        api(`/subscriptions/upgrade-options?child_id=${child.id}`)
          .then(u => setUpgrade(u?.options?.length ? u : null))
          .catch(() => setUpgrade(null))
      );
    }

    Promise.all(fetches).finally(() => setLoadingDetails(false));
  }, [activeChild, childrenProp]);

  // Compute display values from subscription
  const subType = subscription ? planLabel(subscription.plan_months) : "";
  const subEnd = subscription && subscription.expires_at
    ? fmtDate(subscription.expires_at)
    : "";
  // Days left ONLY once the plan has actually started (questionnaire done) → drives
  // the pre-expiry «تمدید اشتراک» button so parents renew without a gap.
  const renewDaysLeft = child && child.questionnaire_completed && subscription && subscription.expires_at
    ? Math.ceil((new Date(subscription.expires_at) - Date.now()) / 86400000)
    : null;

  const storiesHeard = stories.filter(s => s.listened).length;
  const storiesTotal = stories.length;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, position: "relative" }}>
      <div style={{ position: "fixed", inset: 0, background: `linear-gradient(180deg, ${C.bgDeep} 0%, ${C.bg} 40%)`, zIndex: 0, pointerEvents: "none" }}>
        <Stars count={30} />
      </div>

      <div style={{ position: "relative", zIndex: 1, padding: "20px 20px 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FluentEmoji anim="float" size={22}>🌙</FluentEmoji>
            <div>
              <div style={{ color: C.gold, fontSize: 21, fontWeight: 800 }}>{t("ضحا قصه")}</div>
              <div style={{ color: C.textMid, fontSize: 14, marginTop: 2 }}>{userName ? t("سلام {name} جانِ مهربان", { name: userName }) : t("سلام مهربان‌جان")} 👋</div>
            </div>
          </div>
          {hasNamedChild && (
            <button onClick={onAddChild} className="press"
              style={{ background: C.borderGold, border: `1px solid ${C.gold}44`, color: C.gold, borderRadius: 12, padding: "9px 15px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT, display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={16} /> {t("فرزند جدید")}
            </button>
          )}
        </div>
        <InfoBanner />

        {/* Child Tabs — hidden for a parent who hasn't taken any step yet: a lone
            «نام فرزند» tab is only a distraction from step 1 (product owner, 2026-10-29). */}
        {!(noStepDone && !hasNamedChild) && (
        <div style={{ display: "flex", gap: 10, overflowX: "auto" }}>
          {tabs.map((c, i) => (
            <button key={c.id ?? "virtual"} onClick={() => setActiveChild(i)} className="press"
              style={{
                display: "flex", alignItems: "center", gap: 8,
                background: activeChild === i ? `${C.gold}18` : "transparent",
                border: activeChild === i ? `1.5px solid ${C.gold}` : `1.5px solid ${C.border}`,
                borderBottom: activeChild === i ? `1.5px solid ${C.bg}` : `1.5px solid ${C.border}`,
                borderRadius: "14px 14px 0 0", padding: "11px 19px",
                color: activeChild === i ? C.gold : C.textMid,
                fontFamily: FONT, fontSize: 16, fontWeight: activeChild === i ? 700 : 400,
                cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.2s",
              }}>
              <EmojiAvatar size={20}>{c.avatar}</EmojiAvatar>
              <span style={c.name ? undefined : { opacity: 0.75 }}>{c.name || t("نام فرزند")}</span>
              {(c.new_story_count > 0 || (i === activeChild && stories.some(s => s.is_new))) && <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.teal, display: "inline-block", animation: "pulseDot 1.5s ease-in-out infinite" }} />}
            </button>
          ))}
        </div>
        )}
      </div>

      <div key={activeChild} style={{ position: "relative", zIndex: 1, padding: "0 16px 100px", animation: "slideUp 0.35s ease" }}>
        {/* Child card */}
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: "16px", padding: 20, marginBottom: 16, position: "relative" }}>
          {!noStepDone && (
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <button onClick={() => (child.id ? onEditChild(child) : onAddChild())} className="press editTap"
              style={{ display: "flex", alignItems: "center", gap: 14, background: "none", border: "none", padding: "6px 8px", margin: "-6px -8px", borderRadius: 14, cursor: "pointer", fontFamily: FONT, textAlign: "right" }}>
              <div className="avatarPop" style={{ position: "relative", width: 58, height: 58, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 30 }}>
                <SquircleBg fill={`${C.gold}22`} stroke={`${C.gold}44`} strokeWidth={2} />
                <span style={{ position: "relative", zIndex: 1, display: "flex" }}><EmojiAvatar size={34}>{child.avatar}</EmojiAvatar></span>
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: named ? C.cream : C.textMid, fontSize: 20, fontWeight: 700 }}>{child.name || t("نام فرزند")}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 7, background: `${C.gold}1A`, border: `1px solid ${C.gold}33`, color: C.gold, flexShrink: 0 }}>
                    <Pencil size={12} />
                  </span>
                </div>
                <div style={{ color: C.textMid, fontSize: 15 }}>{named ? child.age : t("هنوز اضافه نشده")}</div>
              </div>
            </button>
            <div style={{ marginRight: "auto" }}>
              {child.virtual ? null : child.subscribed
                ? <span style={{ background: `${C.teal}22`, border: `1px solid ${C.teal}44`, color: C.teal, borderRadius: 999, padding: "6px 14px", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}><Check size={13} /> {t("مشترک")}</span>
                : child.questionnaire_completed
                  ? <span style={{ background: `${C.gold}22`, border: `1px solid ${C.gold}44`, color: C.gold, borderRadius: 999, padding: "6px 14px", fontSize: 13, fontWeight: 600 }}>{t("آمادهٔ شروع")}</span>
                  : <span style={{ background: `${C.red}22`, border: `1px solid ${C.red}44`, color: C.red, borderRadius: 999, padding: "6px 14px", fontSize: 13, fontWeight: 600 }}>{t("بدون اشتراک")}</span>}
            </div>
          </div>
          )}

          {child.subscribed && (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <div style={{ flex: 1, background: C.bg, borderRadius: 12, padding: "11px 14px" }}>
                  <div style={{ color: C.textMid, fontSize: 13 }}>{t("نوع اشتراک")}</div>
                  <div style={{ color: C.gold, fontSize: 16, fontWeight: 700, marginTop: 2 }}>{subType}</div>
                </div>
                <div style={{ flex: 1, background: C.bg, borderRadius: 12, padding: "11px 14px" }}>
                  {/* An active plan always has its questionnaire (payment requires it),
                      so the end date is always real — never a provisional deadline. */}
                  <div style={{ color: C.textMid, fontSize: 13 }}>{t("پایان برنامه")}</div>
                  <div style={{ color: C.cream, fontSize: 15, fontWeight: 600, marginTop: 2 }}>{subEnd}</div>
                </div>
              </div>

              {/* What this month's stories are actually working on (the SEASON focus
                  — services/seasons.py). Showing it is half the point of the season
                  model: the parent can see the plan is theirs, and the monthly
                  check-in that changes it stops feeling like it came from nowhere. */}
              {child.focus && child.questionnaire_completed && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.purple}0F`, border: `1px solid ${C.purple}2E`, borderRadius: 12, padding: "10px 13px", marginBottom: 14 }}>
                  <Target size={15} color={C.purple} style={{ flexShrink: 0 }} />
                  <span style={{ color: C.textMid, fontSize: 13 }}>{t("تمرکز این ماه:")}</span>
                  <span style={{ color: C.cream, fontSize: 13.5, fontWeight: 700 }}>{child.focus}</span>
                </div>
              )}

              {/* Gentle progress bar — measured against the monthly plan
                  (2 stories/week ≈ 8), matching the سفر رشد screen, not the
                  number delivered so far (which made it read "1 of 1"). */}
              {child.questionnaire_completed && (() => {
                const displayTotal = 8;
                const heard = Math.min(storiesHeard, displayTotal);
                const pct = (heard / displayTotal) * 100;
                return (
                  <button onClick={() => onJourney(child)} className="press" style={{ width: "100%", background: `${C.gold}0D`, border: `1px solid ${C.borderGold}`, borderRadius: 12, padding: "12px 14px", cursor: "pointer", textAlign: "right" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <span style={{ color: C.cream, fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                        <TrendingUp size={15} color={C.gold} /> {t("سفر رشد {name}", { name: child.name })}
                      </span>
                      <span style={{ color: C.gold, fontSize: 13, fontWeight: 700 }}>{t("{a} از {b}", { a: fmt(heard), b: fmt(displayTotal) })}</span>
                    </div>
                    <div style={{ height: 6, background: C.border, borderRadius: 6, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`, borderRadius: 6, transition: "width 0.6s ease" }} />
                    </div>
                  </button>
                );
              })()}
            </>
          )}

          {/* The three steps, until all three are done. */}
          {!setupDone && (
            <div style={{ marginTop: child.subscribed ? 14 : 0 }}>
              <SetupSteps child={child} onAddChild={onAddChild} onEditChild={onEditChild}
                onQuestionnaire={onQuestionnaire} onSubscribe={onSubscribe} guest={guest} />
            </div>
          )}
        </div>

        {/* Action buttons — the funnel is QUESTIONNAIRE FIRST, then payment.
            The parent invests the answers (free, nothing is generated), and only then
            are they asked for money — so the plans screen arrives after they're
            already committed, not before. */}
        {renewDaysLeft != null && renewDaysLeft <= 14 ? (
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => onSubscribe(child)} className="press celebrate glow"
              style={{ width: "100%", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, border: "none", borderRadius: 16, padding: "16px 0", fontSize: 17, fontWeight: 800, cursor: "pointer", fontFamily: FONT, boxShadow: `0 4px 24px ${C.gold}44`, marginBottom: 7, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <Star size={19} fill={C.bgDeep} /> {t("تمدید اشتراک {name}", { name: child.name })}
            </button>
            <div style={{ color: C.textMid, fontSize: 12.5, textAlign: "center", lineHeight: 1.6 }}>
              {renewDaysLeft <= 0 ? t("اشتراک امروز به پایان می‌رسد") : t("{n} روز تا پایان اشتراک", { n: toFa(renewDaysLeft) })} · {t("با تمدید، قصه‌ها بدون وقفه ادامه می‌یابند")}
            </div>
          </div>
        ) : setupDone ? (
          // Renewing is possible ANY time during a plan (product owner, 2026-10-27);
          // the new plan is queued behind the current one, so a quiet button is
          // enough until the last two weeks, when the gold one above takes over.
          <button onClick={() => onSubscribe(child)} className="press"
            style={{ width: "100%", background: "transparent", border: `1px solid ${C.borderGold}`, color: C.gold, borderRadius: 16, padding: "12px 0", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Star size={16} /> {t("تمدید یا خرید طرح بعدی")}
          </button>
        ) : null}

        {/* Upgrade — lengthen the running plan for the price difference. NOT a
            renewal (that queues a new plan); shown while there is a longer plan to go to. */}
        {upgrade && onUpgrade && (
          <button onClick={() => onUpgrade(child, upgrade)} className="press"
            style={{ width: "100%", background: `${C.purple}14`, border: `1px solid ${C.purple}66`, color: C.purple, borderRadius: 16, padding: "12px 0", fontSize: 14.5, fontWeight: 700, cursor: "pointer", fontFamily: FONT, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Sparkles size={16} /> {t("ارتقا به طرح بلندتر — فقط با پرداخت مابه‌التفاوت")}
          </button>
        )}

        {/* «قصه‌های تعاملی» — the door into the 3D tale library (a live preview of
            the child's first tale). Ranked per child server-side; the first ones are
            free for everyone, the rest carry a padlock on the shelf. */}
        {onTale && <TaleShelf child={child} onOpen={onTale} />}

        {/* «بازی‌ها» — calm games chosen from THIS child's questionnaire. Shown to
            everyone: before the questionnaire it explains they come from it; after
            it the first levels are free, which is a reason to fill it in AND to buy. */}
        {onGames && (
          <button onClick={() => { haptic("light"); onGames(child); }} className="press"
            style={{ width: "100%", background: `linear-gradient(135deg, ${C.purple}22, ${C.teal}18)`, border: `1px solid ${C.purple}55`, borderRadius: 16, padding: "13px 16px", cursor: "pointer", fontFamily: FONT, marginBottom: 12, display: "flex", alignItems: "center", gap: 12, textAlign: "start" }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", color: C.cream, fontSize: 15.5, fontWeight: 800 }}>{named ? t("بازی‌های {name}", { name: child.name }) : t("بازی‌های آرام")}</span>
              <span style={{ display: "block", color: C.textMid, fontSize: 12.5, marginTop: 2, lineHeight: 1.6 }}>
                {child.questionnaire_completed
                  ? t("بازی‌های آرام، مخصوصِ ویژگی‌های همین کودک")
                  : t("۱۱ بازی آرام — بعد از قدم ۱ و ۲، مخصوصِ کودک شما چیده می‌شوند")}
              </span>
              {/* a peek at who lives in the games */}
              <span style={{ display: "flex", marginTop: 8 }}>
                {GAME_PEEK.map((g, i) => (
                  <img key={g} src={`/games/k/${g}.webp`} alt="" width={34} height={34} loading="lazy" decoding="async"
                    style={{ width: 34, height: 34, objectFit: "contain", marginInlineStart: i ? -6 : 0, filter: `drop-shadow(0 2px 4px ${alpha(BLACK, .35)})` }} />
                ))}
              </span>
            </span>
            <span style={{ color: C.purple, fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{child.questionnaire_completed ? t("بازی") : t("نگاه کن")}</span>
          </button>
        )}

        {/* Schedule + countdown (only when subscribed + has schedule) */}
        {child.subscribed && schedule && (
          <ScheduleWidget schedule={schedule} childName={child.name} />
        )}

        {/* Add to the phone's home screen — offered ONLY when the host can do it AND
            the app is not already there (status "missed"). A service whose value
            arrives twice a week lives or dies on being easy to re-open. */}
        {child.subscribed && homeScreenOffer && !homeAdded && (
          <button onClick={() => { haptic("light"); addToHomeScreen(); setHomeAdded(true); }} className="press"
            style={{ width: "100%", background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: "13px 0", fontSize: 14.5, fontWeight: 600, cursor: "pointer", fontFamily: FONT, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: C.creamDim }}>
            <Smartphone size={16} /> {t("افزودن ضحا قصه به صفحهٔ اصلی")}
          </button>
        )}

        {/* Referral button */}
        {child.subscribed && (
          <button onClick={onReferral} className="press"
            style={{ width: "100%", background: C.bgCard, border: `1px solid ${C.borderGold}`, borderRadius: 16, padding: "13px 0", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: FONT, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: C.gold }}>
            <Gift size={17} /> {t("دعوت دوستان و تخفیف رایگان")}
          </button>
        )}

        {/* Stories */}
        <div style={{ marginTop: 8 }}>
          <div style={{ color: C.textMid, fontSize: 15, marginBottom: 12, paddingRight: 4 }}>{named ? t("داستان‌های {name}", { name: child.name }) : t("قصه‌های شخصی")}</div>

          {stories.length === 0 ? (
            <div style={{ background: C.bgCard, border: `1px dashed ${C.border}`, borderRadius: 16, padding: 32, textAlign: "center" }}>
              <FluentEmoji anim="float" size={42} style={{ marginBottom: 12 }}>{!child.subscribed ? "⭐" : "🌙"}</FluentEmoji>
              <div style={{ color: C.textMid, fontSize: 16 }}>
                {!named
                  ? t("قصه‌های شخصی با همان سه قدم بالا شروع می‌شوند")
                  : !child.subscribed
                  ? !child.questionnaire_completed
                    ? t("برای دریافت داستان‌های مخصوص {name}، ابتدا پرسشنامه را تکمیل کنید", { name: child.name })
                    : t("پرسشنامهٔ {name} آماده است — با تهیهٔ اشتراک، قصه‌ها شروع می‌شوند", { name: child.name })
                  : !child.questionnaire_completed
                  ? t("قصه‌ها بعد از تکمیل پرسشنامه شروع می‌شوند")
                  : t("اولین داستان به‌زودی آماده می‌شود")}
              </div>
              <div style={{ color: C.textDim, fontSize: 14, marginTop: 4 }}>
                {child.subscribed && child.questionnaire_completed
                  ? t("داستان‌ها دو بار در هفته ارسال می‌شوند")
                  : t("با اشتراک فعال، هفته‌ای دو قصه دریافت می‌کنید")}
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {stories.map(s => (
                <button key={s.id} onClick={() => onSelectStory(s, child)} className="press cardHover"
                  style={{ background: C.bgCard, border: `1px solid ${s.is_new ? C.teal + "66" : C.border}`, borderRadius: 16, padding: "17px 18px", display: "flex", alignItems: "center", gap: 14, cursor: "pointer", textAlign: "right", width: "100%", transition: "all 0.2s", boxShadow: s.is_new ? `0 0 16px ${C.teal}22` : "none" }}>
                  <div style={{ width: 46, height: 46, borderRadius: 14, background: s.is_new ? `${C.teal}22` : `${C.gold}11`, border: `1.5px solid ${s.is_new ? C.teal + "55" : C.borderGold}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {s.listened ? <Music size={20} color={C.gold} /> : <Play size={20} color={C.teal} fill={C.teal} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.cream, fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{s.title}</div>
                    <div style={{ color: C.textMid, fontSize: 14 }}>{s.assigned_at ? fmtDate(s.assigned_at) : ""}</div>
                  </div>
                  {s.is_new && <span style={{ background: C.teal, color: C.bgDeep, borderRadius: 999, padding: "4px 12px", fontSize: 13, fontWeight: 700, flexShrink: 0, animation: "pulseDot 1.8s ease-in-out infinite" }}>{t("جدید")}</span>}
                </button>
              ))}
            </div>
          )}

          {!guest && <SupportHint onSupport={onSupport} subscribed={children.some(c => c.subscribed)} />}

          {/* Terms + language — these live in the host's ⋮ «تنظیمات» sheet now, so
              they only appear here on a host that has no SettingsButton to put them
              behind. Without this fallback, language would be unreachable there. */}
          {!hasSettingsButton() && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 26, marginBottom: 8 }}>
              <button onClick={onTerms} style={{ background: "none", border: "none", color: C.textMid, fontSize: 13, cursor: "pointer", fontFamily: FONT, textDecoration: "underline", textUnderlineOffset: 3 }}>
                {t("قوانین و مقررات")}
              </button>
              {onGuarantee && (
                <button onClick={onGuarantee} style={{ background: "none", border: "none", color: C.textMid, fontSize: 13, cursor: "pointer", fontFamily: FONT, textDecoration: "underline", textUnderlineOffset: 3 }}>
                  {t("ضمانت بازگشت وجه")}
                </button>
              )}
              <LangToggle />
            </div>
          )}

          {/* Ads are the FREE version's: gone the moment any child has a plan. Pinned
              to the bottom of the screen; its spacer is the page's last row. */}
          <AdSlot show={!children.some(c => c.subscribed)} />
        </div>
      </div>
    </div>
  );
}

